import { useEffect, useState, useContext, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { collection, doc, query, orderBy, onSnapshot } from "firebase/firestore";
import { callFunction } from "../api/callFunction";
import { toast } from "react-toastify";
import { db } from "../firebase";
import { GlobalContext } from "../context/GlobalContext";
import { Dialog, DialogBackdrop, DialogPanel } from "@headlessui/react";
import {
  toMillis,
  formatRelativeTime,
  formatTimestamp,
  MessageThread,
} from "../components/MessageThread";

const EMPTY_LOG = { reportedUserID: "", reportedName: "", reportedByName: "", note: "", eventId: "" };

const STATUS_META = {
  open: { label: "Open", cls: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300" },
  under_review: { label: "Under review", cls: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300" },
  resolved: { label: "Resolved", cls: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300" },
};

const DECISION_META = {
  dismissed: { label: "Dismissed", cls: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300" },
  warning: { label: "Warning", cls: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300" },
  suspended: { label: "Suspended", cls: "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300" },
  banned: { label: "Banned", cls: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300" },
};

const FLAG_META = {
  warned: { label: "Warned", cls: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300" },
  suspended: { label: "Suspended", cls: "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300" },
  banned: { label: "Banned", cls: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300" },
};

function Badge({ cls, children }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${cls}`}>
      {children}
    </span>
  );
}

export default function Arbitration() {
  const { globalState } = useContext(GlobalContext);
  const isSuperAdmin = globalState.isSuperAdmin;
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [cases, setCases] = useState([]);
  const [selectedCaseId, setSelectedCaseId] = useState(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [openOnly, setOpenOnly] = useState(false);

  // Selected reported user's live thread + moderation rollup.
  const [threadMessages, setThreadMessages] = useState([]);
  const [threadLoading, setThreadLoading] = useState(false);
  const [moderation, setModeration] = useState(null);
  const threadContainerRef = useRef(null);

  const [saving, setSaving] = useState(false);
  const [outcomeDraft, setOutcomeDraft] = useState("");
  const [noteDraft, setNoteDraft] = useState("");
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);

  // "Log incident" modal — for reports made outside the app.
  const [showLog, setShowLog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [logForm, setLogForm] = useState(EMPTY_LOG);

  // --- Live list of all arbitration cases (admin-only read, gated by rules). ---
  useEffect(() => {
    if (!isSuperAdmin) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const q = query(collection(db, "arbitrations"), orderBy("updatedAt", "desc"));
    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs.map((d) => {
          const data = d.data();
          return { id: d.id, ...data, _ms: toMillis(data.updatedAt) };
        });
        setCases(rows);
        setLoading(false);
      },
      (err) => {
        console.error("Error loading arbitration cases:", err);
        setLoadError(err?.message || "Failed to load cases.");
        setLoading(false);
      }
    );
    return () => unsubscribe();
  }, [isSuperAdmin]);

  const selectedCase = useMemo(
    () => cases.find((c) => c.id === selectedCaseId) || null,
    [cases, selectedCaseId]
  );

  const reportedUserID = selectedCase?.reportedUserID || null;

  // --- Live message thread with the reported user. ---
  useEffect(() => {
    if (!reportedUserID) {
      setThreadMessages([]);
      return;
    }
    setThreadLoading(true);
    const q = query(
      collection(db, "users", reportedUserID, "messages"),
      orderBy("timestamp", "asc")
    );
    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        const msgs = snap.docs.map((d) => {
          const data = d.data();
          return { id: d.id, ...data, _ms: toMillis(data.timestamp) };
        });
        setThreadMessages(msgs);
        setThreadLoading(false);
        setTimeout(() => {
          if (threadContainerRef.current) {
            threadContainerRef.current.scrollTop = threadContainerRef.current.scrollHeight;
          }
        }, 50);
      },
      (err) => {
        console.error("Error loading thread:", err);
        setThreadLoading(false);
      }
    );
    return () => unsubscribe();
  }, [reportedUserID]);

  // --- Live moderation rollup for the reported user. ---
  useEffect(() => {
    if (!reportedUserID) {
      setModeration(null);
      return;
    }
    const unsubscribe = onSnapshot(
      doc(db, "userModeration", reportedUserID),
      (snap) => setModeration(snap.exists() ? snap.data() : null),
      (err) => console.error("Error loading moderation:", err)
    );
    return () => unsubscribe();
  }, [reportedUserID]);

  // Sync the outcome textarea when switching cases (live edits elsewhere still win
  // via the snapshot, but a fresh selection should show the stored value).
  useEffect(() => {
    setOutcomeDraft(selectedCase?.outcome || "");
    setNoteDraft("");
    setReplyText("");
  }, [selectedCaseId]); // eslint-disable-line react-hooks/exhaustive-deps

  const displayName = (c) =>
    `${c.reportedByName || "Someone"} → ${c.reportedName || "Unknown"}`;

  const filteredCases = useMemo(() => {
    let list = cases;
    if (openOnly) list = list.filter((c) => c.status !== "resolved");
    const ql = searchQuery.trim().toLowerCase();
    if (!ql) return list;
    return list.filter(
      (c) =>
        (c.reportedName && c.reportedName.toLowerCase().includes(ql)) ||
        (c.reportedByName && c.reportedByName.toLowerCase().includes(ql)) ||
        (c.eventName && c.eventName.toLowerCase().includes(ql)) ||
        (c.note && c.note.toLowerCase().includes(ql)) ||
        (c.reportedUserID && c.reportedUserID.toLowerCase().includes(ql))
    );
  }, [cases, searchQuery, openOnly]);

  const openCount = useMemo(
    () => cases.filter((c) => c.status !== "resolved").length,
    [cases]
  );

  // Other cases for the same reported user (the "repeat offender" history).
  const userHistory = useMemo(() => {
    if (!reportedUserID) return [];
    return cases.filter((c) => c.reportedUserID === reportedUserID && c.id !== selectedCaseId);
  }, [cases, reportedUserID, selectedCaseId]);

  const callUpdate = async (patch) => {
    if (!selectedCaseId) return;
    setSaving(true);
    try {
      await callFunction("adminUpdateArbitration", { caseId: selectedCaseId, ...patch }, { timeout: 60000 });
    } catch (err) {
      console.error("adminUpdateArbitration failed:", err);
      toast.error(err?.message || "Update failed.");
    } finally {
      setSaving(false);
    }
  };

  const submitNote = () => {
    const text = noteDraft.trim();
    if (!text) return;
    callUpdate({ addNote: text });
    setNoteDraft("");
  };

  const sendReply = async () => {
    if (!replyText.trim() || !reportedUserID) return;
    setSending(true);
    try {
      const data = await callFunction(
        "adminSendMessage",
        {
          userId: reportedUserID,
          body: replyText.trim(),
          eventId: selectedCase?.eventId || null,
          topic: `Report review${selectedCase?.eventName ? ` — ${selectedCase.eventName}` : ""}`,
        },
        { timeout: 120000 }
      );
      setReplyText("");
      if (data?.relayNote) toast.info(data.relayNote);
      else toast.success("Message sent.");
    } catch (err) {
      console.error("adminSendMessage failed:", err);
      toast.error(err?.message || "Failed to send message.");
    } finally {
      setSending(false);
    }
  };

  const openUser = (userID) => {
    if (!userID) return;
    navigate(`/users?q=${encodeURIComponent(userID)}`);
  };

  const createIncident = async () => {
    if (!logForm.reportedUserID.trim() || !logForm.note.trim()) return;
    setCreating(true);
    try {
      const data = await callFunction(
        "adminCreateArbitration",
        {
          reportedUserID: logForm.reportedUserID.trim(),
          reportedName: logForm.reportedName.trim() || null,
          reportedByName: logForm.reportedByName.trim() || null,
          note: logForm.note.trim(),
          eventId: logForm.eventId.trim() || null,
        },
        { timeout: 60000 }
      );
      toast.success("Incident logged.");
      setShowLog(false);
      setLogForm(EMPTY_LOG);
      if (data?.caseId) setSelectedCaseId(data.caseId);
    } catch (err) {
      console.error("adminCreateArbitration failed:", err);
      toast.error(err?.message || "Failed to log incident.");
    } finally {
      setCreating(false);
    }
  };

  if (!isSuperAdmin) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 mt-6">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Arbitration</h1>
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">This view is only available to super admins.</p>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="mt-4 sm:mt-0 flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Arbitration</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Safety reports filed between attendees, with the adjudication record for each.
            {cases.length > 0 && <span> {cases.length} cases.</span>}
          </p>
        </div>
        <button
          onClick={() => setShowLog(true)}
          className="flex-shrink-0 px-3 py-2 rounded-md text-sm font-medium bg-primary text-white hover:bg-primaryDark"
        >
          + Log incident
        </button>
      </div>

      <div className="mt-4 flex flex-col lg:flex-row gap-4 h-[calc(100vh-180px)]">
        {/* Case list */}
        <div className="lg:w-80 flex-shrink-0 flex flex-col border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 overflow-hidden">
          <div className="p-3 border-b border-gray-200 dark:border-gray-700 space-y-2">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search name, event, or reason…"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-500"
            />
            <button
              onClick={() => setOpenOnly((v) => !v)}
              className={`w-full px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                openOnly ? "bg-amber-500 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-600"
              }`}
            >
              {openOnly ? "Showing unresolved" : "Unresolved only"}
              {openCount > 0 && ` (${openCount})`}
            </button>
          </div>

          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
              </div>
            ) : loadError ? (
              <div className="p-4 text-sm text-red-600 dark:text-red-400">{loadError}</div>
            ) : filteredCases.length === 0 ? (
              <div className="p-4 text-sm text-gray-400 dark:text-gray-500 text-center">No cases found.</div>
            ) : (
              filteredCases.map((c) => {
                const isSelected = c.id === selectedCaseId;
                const sm = STATUS_META[c.status] || STATUS_META.open;
                const dm = c.decision ? DECISION_META[c.decision] : null;
                return (
                  <button
                    key={c.id}
                    onClick={() => setSelectedCaseId(c.id)}
                    className={`w-full text-left px-3 py-3 border-b border-gray-100 dark:border-gray-800 transition-colors ${
                      isSelected ? "bg-primary/10" : "hover:bg-gray-50 dark:hover:bg-gray-700"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium text-gray-900 dark:text-gray-100 truncate">{displayName(c)}</span>
                      <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">
                        {formatRelativeTime(c._ms)}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 mt-1">
                      <Badge cls={sm.cls}>{sm.label}</Badge>
                      {dm && <Badge cls={dm.cls}>{dm.label}</Badge>}
                      {c.eventName && (
                        <span className="text-xs text-gray-500 dark:text-gray-400 truncate">{c.eventName}</span>
                      )}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* Detail pane */}
        <div className="flex-1 flex flex-col border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 overflow-hidden min-h-0">
          {!selectedCase ? (
            <div className="flex-1 flex items-center justify-center text-gray-400 dark:text-gray-500 text-sm">
              Select a case to review it.
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto p-4 space-y-5">
              {/* Header */}
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-semibold text-gray-900 dark:text-gray-100 text-lg">{displayName(selectedCase)}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {selectedCase.eventName || "Unknown event"} · updated{" "}
                    {formatRelativeTime(selectedCase._ms)}
                  </div>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <Badge cls={(STATUS_META[selectedCase.status] || STATUS_META.open).cls}>
                    {(STATUS_META[selectedCase.status] || STATUS_META.open).label}
                  </Badge>
                  {selectedCase.decision && DECISION_META[selectedCase.decision] && (
                    <Badge cls={DECISION_META[selectedCase.decision].cls}>
                      {DECISION_META[selectedCase.decision].label}
                    </Badge>
                  )}
                </div>
              </div>

              {/* Reported-user flag banner */}
              {moderation && (moderation.strikes > 0 || (moderation.totalCaseCount || 0) > 1) && (
                <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 px-3 py-2 text-sm text-amber-800 dark:text-amber-200 flex items-center gap-2 flex-wrap">
                  <span className="font-semibold">{selectedCase.reportedName || "This user"}:</span>
                  {FLAG_META[moderation.status] && <Badge cls={FLAG_META[moderation.status].cls}>{FLAG_META[moderation.status].label}</Badge>}
                  <span>{moderation.strikes || 0} strike{(moderation.strikes || 0) === 1 ? "" : "s"}</span>
                  <span>·</span>
                  <span>
                    {moderation.openCaseCount || 0} open / {moderation.totalCaseCount || 0} total cases
                  </span>
                </div>
              )}

              {/* The report */}
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 space-y-2">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400">Reported person</p>
                    <p className="text-sm text-gray-900 dark:text-gray-100">
                      {selectedCase.reportedName || "Unknown"}{" "}
                      {selectedCase.reportedUserID && (
                        <button
                          className="text-primary hover:underline text-xs font-mono"
                          onClick={() => openUser(selectedCase.reportedUserID)}
                          title="Open in Users"
                        >
                          {selectedCase.reportedUserID}
                        </button>
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400">Reported by</p>
                    <p className="text-sm text-gray-900 dark:text-gray-100">
                      {selectedCase.reportedByName || "Unknown"}{" "}
                      {selectedCase.reportedByUserID && (
                        <button
                          className="text-primary hover:underline text-xs font-mono"
                          onClick={() => openUser(selectedCase.reportedByUserID)}
                          title="Open in Users"
                        >
                          {selectedCase.reportedByUserID}
                        </button>
                      )}
                    </p>
                  </div>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400">Reason</p>
                  <p className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">{selectedCase.note || "(none)"}</p>
                </div>
              </div>

              {/* Adjudication controls */}
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 space-y-3">
                <p className="text-sm font-semibold text-gray-700 dark:text-gray-200">Adjudication</p>
                <div className="flex flex-wrap gap-3">
                  <label className="text-sm">
                    <span className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Status</span>
                    <select
                      value={selectedCase.status || "open"}
                      disabled={saving}
                      onChange={(e) => callUpdate({ status: e.target.value })}
                      className="px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                    >
                      <option value="open">Open</option>
                      <option value="under_review">Under review</option>
                      <option value="resolved">Resolved</option>
                    </select>
                  </label>
                  <label className="text-sm">
                    <span className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Decision</span>
                    <select
                      value={selectedCase.decision || ""}
                      disabled={saving}
                      onChange={(e) => callUpdate({ decision: e.target.value || null })}
                      className="px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                    >
                      <option value="">— none —</option>
                      <option value="dismissed">Dismissed</option>
                      <option value="warning">Warning</option>
                      <option value="suspended">Suspended</option>
                      <option value="banned">Banned</option>
                    </select>
                  </label>
                </div>

                <div>
                  <span className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Outcome</span>
                  <textarea
                    value={outcomeDraft}
                    onChange={(e) => setOutcomeDraft(e.target.value)}
                    rows={2}
                    placeholder="What we decided and why…"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-500"
                  />
                  <button
                    onClick={() => callUpdate({ outcome: outcomeDraft })}
                    disabled={saving || outcomeDraft === (selectedCase.outcome || "")}
                    className="mt-1 px-3 py-1.5 rounded-md text-sm font-medium bg-primary text-white disabled:opacity-40"
                  >
                    Save outcome
                  </button>
                </div>

                {/* Admin notes log */}
                {Array.isArray(selectedCase.adminNotes) && selectedCase.adminNotes.length > 0 && (
                  <div className="space-y-1.5">
                    <span className="block text-xs text-gray-500 dark:text-gray-400">Notes</span>
                    {[...selectedCase.adminNotes]
                      .sort((a, b) => (a.at || 0) - (b.at || 0))
                      .map((n, i) => (
                        <div key={i} className="text-sm text-gray-700 dark:text-gray-200 bg-gray-50 dark:bg-gray-800 rounded px-2 py-1">
                          <span className="text-[10px] text-gray-400 dark:text-gray-500">{formatTimestamp(n.at)}</span>
                          <p className="whitespace-pre-wrap">{n.text}</p>
                        </div>
                      ))}
                  </div>
                )}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={noteDraft}
                    onChange={(e) => setNoteDraft(e.target.value)}
                    placeholder="Add an internal note…"
                    className="flex-1 px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-500"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") submitNote();
                    }}
                  />
                  <button
                    onClick={submitNote}
                    disabled={saving || !noteDraft.trim()}
                    className="px-3 py-1.5 rounded-md text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-600 disabled:opacity-40"
                  >
                    Add note
                  </button>
                </div>
              </div>

              {/* Reported user's other cases */}
              {userHistory.length > 0 && (
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 space-y-2">
                  <p className="text-sm font-semibold text-gray-700 dark:text-gray-200">
                    Other cases for {selectedCase.reportedName || "this user"} ({userHistory.length})
                  </p>
                  {userHistory.map((c) => {
                    const sm = STATUS_META[c.status] || STATUS_META.open;
                    const dm = c.decision ? DECISION_META[c.decision] : null;
                    return (
                      <button
                        key={c.id}
                        onClick={() => setSelectedCaseId(c.id)}
                        className="w-full text-left flex items-center gap-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 rounded px-2 py-1"
                      >
                        <Badge cls={sm.cls}>{sm.label}</Badge>
                        {dm && <Badge cls={dm.cls}>{dm.label}</Badge>}
                        <span className="text-gray-600 dark:text-gray-300 truncate">
                          {c.eventName || "Unknown event"} · by {c.reportedByName || "Unknown"}
                        </span>
                        <span className="ml-auto text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">
                          {formatRelativeTime(c._ms)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Message thread + reply */}
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700 text-sm font-semibold text-gray-700 dark:text-gray-200">
                  Thread with {selectedCase.reportedName || "user"}
                </div>
                {!reportedUserID ? (
                  <div className="p-4 text-sm text-gray-400 dark:text-gray-500">No user id on this report.</div>
                ) : (
                  <>
                    <MessageThread
                      messages={threadMessages}
                      loading={threadLoading}
                      containerRef={threadContainerRef}
                      emptyText="No messages with this member yet."
                      className="max-h-80 overflow-y-auto p-4 bg-gray-50 dark:bg-gray-800 space-y-3"
                    />
                    <div className="p-3 border-t border-gray-200 dark:border-gray-700 flex gap-2">
                      <textarea
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        rows={2}
                        placeholder="Reply to the member (sends an SMS and opens a reply relay)…"
                        className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-500"
                      />
                      <button
                        onClick={sendReply}
                        disabled={sending || !replyText.trim()}
                        className="px-4 rounded-md text-sm font-medium bg-primary text-white disabled:opacity-40"
                      >
                        {sending ? "Sending…" : "Send"}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Log incident — for reports made outside the app (email, in person, support) */}
      <Dialog open={showLog} onClose={() => !creating && setShowLog(false)} className="relative z-50">
        <DialogBackdrop className="fixed inset-0 bg-black/30" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <DialogPanel className="w-full max-w-lg rounded-lg bg-white dark:bg-gray-800 p-5 shadow-xl space-y-3">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Log an incident</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                For a report made outside the app (email, in person, support). Creates an open case
                you can then adjudicate like any other.
              </p>
            </div>

            <label className="block">
              <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">Reported user ID *</span>
              <input
                type="text"
                value={logForm.reportedUserID}
                onChange={(e) => setLogForm((f) => ({ ...f, reportedUserID: e.target.value }))}
                placeholder="The member being reported — find their ID in Users"
                className="mt-1 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-500"
              />
            </label>

            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">Reported name</span>
                <input
                  type="text"
                  value={logForm.reportedName}
                  onChange={(e) => setLogForm((f) => ({ ...f, reportedName: e.target.value }))}
                  placeholder="(auto-filled if blank)"
                  className="mt-1 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-500"
                />
              </label>
              <label className="block">
                <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">Reported by</span>
                <input
                  type="text"
                  value={logForm.reportedByName}
                  onChange={(e) => setLogForm((f) => ({ ...f, reportedByName: e.target.value }))}
                  placeholder="name or source (e.g. venue email)"
                  className="mt-1 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-500"
                />
              </label>
            </div>

            <label className="block">
              <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">What happened *</span>
              <textarea
                value={logForm.note}
                onChange={(e) => setLogForm((f) => ({ ...f, note: e.target.value }))}
                rows={4}
                placeholder="Describe the incident as reported…"
                className="mt-1 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-500"
              />
            </label>

            <label className="block">
              <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">Related event ID (optional)</span>
              <input
                type="text"
                value={logForm.eventId}
                onChange={(e) => setLogForm((f) => ({ ...f, eventId: e.target.value }))}
                placeholder="if the incident happened at a specific event"
                className="mt-1 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-500"
              />
            </label>

            <div className="flex justify-end gap-2 pt-1">
              <button
                onClick={() => setShowLog(false)}
                disabled={creating}
                className="px-3 py-2 rounded-md text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-600 disabled:opacity-40"
              >
                Cancel
              </button>
              <button
                onClick={createIncident}
                disabled={creating || !logForm.reportedUserID.trim() || !logForm.note.trim()}
                className="px-4 py-2 rounded-md text-sm font-medium bg-primary text-white disabled:opacity-40"
              >
                {creating ? "Logging…" : "Log incident"}
              </button>
            </div>
          </DialogPanel>
        </div>
      </Dialog>
    </div>
  );
}
